import { ContentState } from "draft-js";
import { I18n } from "@assets/strings";
import React, { useState } from "react";
import styled from "styled-components";
import { useServices } from "@core/servicesContext";
import {
	allDiplomaModes,
	allDiplomaTypes,
	Diploma,
	DiplomaDraft,
	DiplomaMode,
	DiplomaType,
} from "@domain/organization/track/diploma";
import { Track } from "@domain/organization/track/track";
import { useAsync } from "@utils/hooks";
import { LoadingButton } from "@ui/components/buttons/loadingButton";
import { FormRichInput } from "@ui/components/inputs/draft/formRichInput";
import { useForm } from "@ui/components/inputs/hooks";
import { Selecter } from "@ui/components/inputs/selecter";
import { SimpleTextInput } from "@ui/components/inputs/simpleTextInput";
import { stack } from "@ui/components/layout";
import { ClosableModal } from "@ui/modals/closableModal";
import { gap } from "@ui/styles/utils";
import { createToast } from "@ui/toasts/utils";

interface DiplomaEditorModalProps extends React.HTMLAttributes<HTMLDivElement> {
	initialDiploma?: Diploma;
	track: Track;
	levelId: string;
	close: () => void;
}
export const DiplomaEditorModal: React.FC<DiplomaEditorModalProps> = ({
	close,
	track,
	levelId,
	initialDiploma,
	...htmlProps
}) => {
	const { trackService } = useServices();

	const { fieldProps, formValue, checkSubmit } = useForm(
		{ title: initialDiploma?.title ?? "", duration: initialDiploma?.duration ?? "" },
		["title"],
		{
			title: (v) => v.length < 128,
			duration: (v) => v.length < 64,
		}
	);
	const [description, setDescription] = useState(initialDiploma?.description ?? ContentState.createFromText(""));
	const [type, setType] = useState<DiplomaType>(initialDiploma?.type ?? "INITIAL");
	const [mode, setMode] = useState<DiplomaMode>(initialDiploma?.mode ?? "FACE_TO_FACE");

	const { loading, run } = useAsync(
		(diploma: DiplomaDraft) => trackService.saveDiploma(track, levelId, diploma, initialDiploma?.id),
		[track, levelId, initialDiploma]
	);

	return (
		<Container
			close={close}
			title={initialDiploma ? I18n.t("organization.diploma.edit") : I18n.t("organization.diploma.new")}
			keyboardEventDisabled
			{...htmlProps}
		>
			<form
				onSubmit={async (e) => {
					e.preventDefault();
					if (type && mode && checkSubmit()) {
						await run({ title: formValue.title, description, duration: formValue.duration, type, mode });
						createToast(
							I18n.t("success"),
							I18n.t(initialDiploma ? "organization.diploma.save.success" : "organization.diploma.create.success"),
							"success"
						);
						close();
					}
				}}
			>
				<SimpleTextInput
					{...fieldProps("title")}
					placeholder={I18n.t("organization.diploma.title.placeholder") + "*"}
				/>
				<FormRichInput
					value={description}
					onChange={(value) => setDescription(value)}
					placeholder={I18n.t("organization.diploma.description.placeholder") + "*"}
				/>
				<SimpleTextInput
					{...fieldProps("duration")}
					placeholder={I18n.t("organization.diploma.duration.placeholder")}
				/>
				<Selecter
					value={type}
					placeholder={I18n.t("organization.diploma.type.placeholder")}
					options={allDiplomaTypes.map((type) => ({ value: type, label: I18n.t(`organization.diploma.type.${type}`) }))}
					onChange={(v) => setType(v as DiplomaType)}
				/>
				<Selecter
					value={mode}
					placeholder={I18n.t("organization.diploma.mode.placeholder")}
					options={allDiplomaModes.map((mode) => ({ value: mode, label: I18n.t(`organization.diploma.mode.${mode}`) }))}
					onChange={(v) => setMode(v as DiplomaMode)}
				/>
				<LoadingButton
					loading={loading}
					type="submit"
					disabled={!formValue.title || !description.getPlainText().trim()}
				>
					{I18n.t(initialDiploma ? "validate" : "create")}
				</LoadingButton>
			</form>
		</Container>
	);
};

const Container = styled(ClosableModal)`
	${gap(40)};
	> form {
		${stack(20, "flex-start", "stretch")};
		> button {
			margin: 0 auto;
		}
	}
`;
